import { DriveItem } from '@/drive/entities/DriveItem/DriveItem';
import { DriveContainerId, DriveItemId } from '@/drive/entities/DriveItemId';

export enum DriveModifierEventType {
  Delete = 'Delete',
  Update = 'Update',
  Add = 'Add',
  Move = 'Move',
  Reorder = 'Reorder',
  AccessChange = 'AccessChange',
}

export enum OptimisticUpdatePhase {
  Apply = 'Apply',
  Error = 'Error',
  Success = 'Success',
}

export interface DriveWriteOptions {
  readOnly?: boolean;
  shouldInheritAccess?: boolean;
}

export interface DriveAddOptions extends DriveWriteOptions {
  withPreview?: boolean;
}

export interface DriveCopyOptions extends DriveWriteOptions {
  precedingNodeId?: DriveItemId;
}

export interface DriveErrorEvent {
  phase: OptimisticUpdatePhase.Error;
  type: DriveModifierEventType;
}

export interface DriveItemPatch {
  id: DriveItemId;
  title: string;
  description: string;
}

export interface DriveUpdateApplyEvent {
  type: DriveModifierEventType.Update;
  phase: OptimisticUpdatePhase.Apply;
  patch: DriveItemPatch;
}

export interface DriveUpdateSuccessEvent {
  type: DriveModifierEventType.Update;
  phase: OptimisticUpdatePhase.Success;
  patch: DriveItemPatch;
}

export interface DriveDeleteApplyEvent {
  type: DriveModifierEventType.Delete;
  phase: OptimisticUpdatePhase.Apply;
  item: DriveItem;
}

export interface DriveDeleteSuccessEvent {
  type: DriveModifierEventType.Delete;
  phase: OptimisticUpdatePhase.Success;
  item: DriveItem;
}

export interface DriveAddApplyEvent {
  type: DriveModifierEventType.Add;
  phase: OptimisticUpdatePhase.Apply;
  temporaryItems: DriveItem[];
  parentId: DriveContainerId;
  precedingNodeIdByTempId: Map<DriveItemId, DriveItemId>;
}

export interface AddSuccessItem {
  tempId: DriveItemId;
  backendItem?: DriveItem;
  error?: string;
}

export interface DriveAddSuccessEvent {
  type: DriveModifierEventType.Add;
  phase: OptimisticUpdatePhase.Success;
  hasOptimisticData: boolean;
  items: AddSuccessItem[];
  parentId: DriveContainerId;
}

export interface DriveMoveApplyEvent {
  type: DriveModifierEventType.Move;
  phase: OptimisticUpdatePhase.Apply;
  item: DriveItem;
  parentId: DriveContainerId;
  previousParentId: DriveContainerId;
}

export interface DriveMoveSuccessEvent {
  type: DriveModifierEventType.Move;
  phase: OptimisticUpdatePhase.Success;
  backendItem: DriveItem;
  parentId: DriveContainerId;
  previousParentId: DriveContainerId;
}

export interface DriveReorderApplyEvent {
  type: DriveModifierEventType.Reorder;
  phase: OptimisticUpdatePhase.Apply;
  parentId: DriveContainerId;
  orderedIds: DriveItemId[];
}

export interface DriveAccessChangeSuccessEvent {
  type: DriveModifierEventType.AccessChange;
  phase: OptimisticUpdatePhase.Success;
  itemId: DriveItemId;
}

export type DriveModifierEvent =
  | DriveErrorEvent
  | DriveUpdateApplyEvent
  | DriveUpdateSuccessEvent
  | DriveDeleteApplyEvent
  | DriveDeleteSuccessEvent
  | DriveAddApplyEvent
  | DriveAddSuccessEvent
  | DriveMoveApplyEvent
  | DriveMoveSuccessEvent
  | DriveReorderApplyEvent
  | DriveAccessChangeSuccessEvent;
